const parseQueryEntries = (searchTerm: string): Array<{ key: string; value: string }> => {
    const normalizedSearchTerm = searchTerm.startsWith('?') ? searchTerm.slice(1) : searchTerm;

    if (normalizedSearchTerm === '') {
        return [];
    }

    return normalizedSearchTerm
        .split('&')
        .filter(Boolean)
        .map(queryEntry => {
            const [rawKey, ...rawValueParts] = queryEntry.split('=');

            return {
                key: decodeURIComponent(rawKey ?? ''),
                value: decodeURIComponent(rawValueParts.join('=')),
            };
        });
};

const serializeQueryEntries = (queryEntries: Array<{ key: string; value: string }>) => {
    const serializedEntries = queryEntries.map(({ key, value }) => {
        const encodedValue = encodeURIComponent(value).replace(/%2B/gi, '+');

        return `${encodeURIComponent(key)}=${encodedValue}`;
    });

    return `?${serializedEntries.join('&')}`;
};

export const updateQueryParameter = (
    searchTerm: string,
    queryParamKey: QUERY_PARAMETER,
    queryParamValue: string
): string => {
    const queryEntries = parseQueryEntries(searchTerm).filter(
        queryEntry => queryEntry.key !== queryParamKey && !isLegacyAlias(queryEntry.key, queryParamKey)
    );

    if (queryParamValue !== '') {
        queryEntries.push({ key: queryParamKey, value: queryParamValue });
    }

    return serializeQueryEntries(queryEntries);
};

export function getQueryParameter(searchTerm: string, queryParamKey: QUERY_PARAMETER): string | null {
    const queryEntries = parseQueryEntries(searchTerm);
    const matchingEntry =
        queryEntries.find(queryEntry => queryEntry.key === queryParamKey) ??
        queryEntries.find(queryEntry => isLegacyAlias(queryEntry.key, queryParamKey));

    return matchingEntry ? matchingEntry.value : null;
}

export const removeQueryParameter = (searchTerm: string, queryParamKey: QUERY_PARAMETER): string => {
    return serializeQueryEntries(
        parseQueryEntries(searchTerm).filter(
            queryEntry => queryEntry.key !== queryParamKey && !isLegacyAlias(queryEntry.key, queryParamKey)
        )
    );
};

export enum QUERY_PARAMETER {
    SEARCH = 'q',
    SORT = 'sort',
    INFO_MODAL = 'infoModal',
    GROUP_SEARCH = 'groupsSearch',
    GROUP_SORT = 'groupsSort',
    USER_SEARCH = 'usersSearch',
    USER_SORT = 'usersSort',
    CREATE_USER_MODAL = 'createUserModal',
    CREATE_GROUP_MODAL = 'createGroupModal',
}

const isLegacyAlias = (key: string, queryParamKey: QUERY_PARAMETER): boolean => {
    return queryParamKey === QUERY_PARAMETER.SEARCH && key === 'search';
};
